import InfoTip from './InfoTip';
import type { SimulationConfig } from '../types/spec';

interface Props {
  config: SimulationConfig;
  status: 'idle' | 'running' | 'paused' | 'completed';
  dataCount: number;
  onConfigChange: (config: SimulationConfig) => void;
  onStart: () => void;
  onPause: () => void;
  onStop: () => void;
  onExportJSON: () => void;
  onExportCSV: () => void;
  onExportAvro: () => void;
  onExportParquet: () => void;
  disabled: boolean;
}

export default function SimulationControls({
  config,
  status,
  dataCount,
  onConfigChange,
  onStart,
  onPause,
  onStop,
  onExportJSON,
  onExportCSV,
  onExportAvro,
  onExportParquet,
  disabled,
}: Props) {
  const isRunning = status === 'running';
  const isPaused = status === 'paused';
  const hasData = dataCount > 0;

  return (
    <section className="ds-sim-controls">
      <h3>Simulation Controls</h3>
      <div className="ds-sim-controls-row">
        <label>
          <InfoTip text="Batch: generate all rows at once. Streaming: generate one row at a time at the configured interval.">
            Mode
          </InfoTip>
          <select
            value={config.mode}
            disabled={isRunning}
            onChange={(e) =>
              onConfigChange({
                ...config,
                mode: e.target.value as 'batch' | 'streaming',
              })
            }
          >
            <option value="batch">Batch</option>
            <option value="streaming">Streaming</option>
          </select>
        </label>

        {config.mode === 'batch' ? (
          <label>
            <InfoTip text="Total number of data rows to generate in one batch.">
              Sample Count
            </InfoTip>
            <input
              type="number"
              min={1}
              max={100000}
              value={config.sampleCount}
              disabled={isRunning}
              onChange={(e) =>
                onConfigChange({
                  ...config,
                  sampleCount: Math.max(1, Number(e.target.value)),
                })
              }
            />
          </label>
        ) : (
          <label>
            <InfoTip text="Time between each generated row in milliseconds (min 2000ms). Data publishing is throttled to at least 2 seconds per send.">
              Interval (ms)
            </InfoTip>
            <input
              type="number"
              min={2000}
              max={60000}
              step={1000}
              value={config.intervalMs}
              disabled={isRunning}
              onChange={(e) =>
                onConfigChange({
                  ...config,
                  intervalMs: Math.max(2000, Number(e.target.value)),
                })
              }
            />
          </label>
        )}

        <div className="ds-sim-btn-group">
          {!isRunning && !isPaused && (
            <button
              className="ds-btn-primary"
              disabled={disabled}
              onClick={onStart}
            >
              ▶ Start
            </button>
          )}
          {isRunning && (
            <button className="ds-btn-secondary" onClick={onPause}>
              ⏸ Pause
            </button>
          )}
          {isPaused && (
            <button className="ds-btn-primary" onClick={onStart}>
              ▶ Resume
            </button>
          )}
          {(isRunning || isPaused) && (
            <button className="ds-btn-stop" onClick={onStop}>
              ⏹ Stop
            </button>
          )}
        </div>

        <div className="ds-sim-status">
          <span className={`ds-status-badge ds-status-${status}`}>
            {status}
          </span>
          <span className="ds-sim-count">{dataCount} rows</span>
        </div>

        {hasData && (
          <div className="ds-sim-btn-group">
            <button className="ds-btn-export" onClick={onExportJSON}>
              ⬇ JSON
            </button>
            <button className="ds-btn-export" onClick={onExportCSV}>
              ⬇ CSV
            </button>
            <button className="ds-btn-export" onClick={onExportAvro}>
              ⬇ Avro
            </button>
            <button className="ds-btn-export" onClick={onExportParquet}>
              ⬇ Parquet
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
